import { Component } from 'react'
import _ from 'lodash'

import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import { Progress, Upload } from 'antd'
import cx from 'classnames'
import styles from './index.css'
import { Icon, Button, Tag } from '@vzmi/shp_shared_compo'
import Image from '../../../../Common/Image'
import { ANT_UPLOAD_STATUS } from '../../../../../constants/globalTypes'
import { Warn } from '../index'
import {
  getIsAnyFileUploading,
  getIsAllFileUploaded,
  getHasAnyErrorFile,
  getIsFileUploading
} from '../../../../../lib/uploaderUtil'
import { MAIN_AND_REVIEW_IMAGE_COUNT } from '../../../Shared'

const Dragger = Upload.Dragger

const UploadButton = (props) => {
  const {
    children,
    className,
    dragUploaderProps,
    ...restProps
  } = props

  return (
    <Dragger
      {...dragUploaderProps}
      className={cx(styles.UploadButton, className)}
    >
      <div
        data-role='image-upload-button'
        {...restProps}
      >
        <Icon
          name='fuji2/plus'
          size={36}
        />
        {children}
      </div>
    </Dragger>
  )
}

const ErrorUploadButton = (props) => {
  const { children, className, message, ...restProps } = props
  return (
    <div
      data-role='error-upload-button'
      {...restProps}
      className={cx(styles.ErrorUploadButton, className)}
    >
      <Icon
        key='icon'
        name='fuji2/plus'
        size={36}
      />
      <div key='warn'>
        <Warn
          className={styles.message}
          message={message}
        />
      </div>
    </div>
  )
}

const UploadListItemInfo = props => {
  const {
    onItemRemove,
    isDisabled,
    file,
    fileIndex,
    triggerUpload
  } = props
  const {
    url,
    thumbnail,
    status,
    percent
  } = file
  const thumbnailUrl = _.get(thumbnail, 'url', url)
  const isMainAndRevieImg = fileIndex < MAIN_AND_REVIEW_IMAGE_COUNT

  const deleteBtn = (
    <Button
      link
      size='medium'
      key='deleteButton'
      data-role='upload-item-delete-button'
      className={styles.deleteButton}
      onClick={() => onItemRemove(file, fileIndex)}
      iconType='delete'
    />
  )

  let tag = null
  let content = null
  let button = null
  if (isMainAndRevieImg) {
    const tagName = (fileIndex === 0) ? '主圖' : '主圖2'
    tag = (
      <Tag
        key='tag'
        className={styles.infoTag}
        color='#525253'
      >
        {tagName}
      </Tag>
    )
  }
  let currentStatus = ''
  const isPercentExist = !_.isUndefined(percent)
  if (
    // antd4 percent 100 status 還是顯示 uploading
    percent === 100 ||
    // 資料轉換 status 還是顯示 uploading 但沒有 percent 代表狀態其實也是上傳完成
    (!isPercentExist && (status === ANT_UPLOAD_STATUS.UPLOADING))
  ) {
    currentStatus = ANT_UPLOAD_STATUS.DONE
  } else {
    // 如果沒有 url 有可能是上傳圖片格式不符合規定，不能列為上傳完成
    // 如果沒有有上傳進度(percent)代表還在上傳中
    const isFailed = (_.isUndefined(url) && !isPercentExist)
    currentStatus = isFailed ? ANT_UPLOAD_STATUS.ERROR : status
  }
  switch (currentStatus) {
    case ANT_UPLOAD_STATUS.DONE: {
      if (thumbnailUrl) {
        content = (
          <Image
            key='content'
            data-role='upload-list-item-thumbnail'
            className={styles.thumbnail}
            src={thumbnailUrl}
          />
        )
        button = deleteBtn
      }
      break
    }
    case ANT_UPLOAD_STATUS.UPLOADING: {
      content = (
        <div
          key='content'
          data-role='is-uploading-sign'
          className={styles.uploadStatus}
        >
          上傳中
        </div>
      )
      break
    }
    case ANT_UPLOAD_STATUS.ERROR: {
      const { error: message } = file
      content = (
        <ErrorUploadButton
          key='content'
          message={message}
          onClick={() => isDisabled ? null : triggerUpload()}
        />
      )
      if (!isMainAndRevieImg) {
        button = deleteBtn
      }
      break
    }
  }

  return (
    <div
      data-role='upload-list-item-info'
      className={styles.UploadListItemInfo}
    >
      {tag}
      {content}
      {!isDisabled && button}
    </div>
  )
}

const UPLOAD_LIST_ITEM_PREFIX = 'upload-list-item'

const UploadListItem = (props) => {
  const {
    file,
    fileIndex,
    isDisabled,
    onItemRemove,
    progress,
    triggerUpload
  } = props

  const infoProps = {
    key: 'info',
    file,
    fileIndex,
    isDisabled,
    onItemRemove,
    triggerUpload
  }
  const { status } = file

  return (
    <div
      data-role={UPLOAD_LIST_ITEM_PREFIX}
      className={cx(styles.UploadListItem, `${UPLOAD_LIST_ITEM_PREFIX}-${status}`, {
        'is-disabled': isDisabled
      })}
    >
      <UploadListItemInfo {...infoProps} />
      {progress}
    </div>
  )
}

const SortableUploadListItem = SortableElement(UploadListItem)

// The reason using react-sortable-hoc instead of react-beautiful-dnd is that the latter not supporting grid dragging.
class UploadList extends Component {
  static defaultProps = {
    progressAttr: {
      strokeWidth: 5,
      showInfo: false,
      strokeLinecap: 'square'
    }
  }

  renderItems = () => {
    const {
      fileList,
      progressAttr,
      disabled,
      triggerUpload,
      onItemRemove
    } = this.props

    const isAllFileUploaded = getIsAllFileUploaded(fileList)
    const isAnyFileUploading = getIsAnyFileUploading(fileList)

    return fileList.map(
      (file, fileIndex) => {
        const {
          uid,
          url,
          percent,
          status
        } = file

        const isUploading = getIsFileUploading(file)
        const isDragDisabled = (isUploading || !isAllFileUploaded || !url)
        const isDisabled = disabled || (isAnyFileUploading && isDragDisabled)

        let progress
        if (status === ANT_UPLOAD_STATUS.UPLOADING) {
        // show loading icon if upload progress listener is disabled
          const loadingProgress = (percent !== 100)
            ? (
              <Progress
                type='line'
                {...progressAttr}
                percent={file.percent}
              />
            )
            : null

          progress = (
            <div
              data-role={'upload-list-item-progress'}
              className={styles.progress}
              key='progress'
            >
              {loadingProgress}
            </div>
          )
        }
        const itemProps = {
          file,
          fileIndex,
          progress,
          onItemRemove,
          triggerUpload,
          isDisabled,
          disabled: isDragDisabled || isDisabled,
          index: fileIndex,
          sortIndex: fileIndex
        }
        return (
          <SortableUploadListItem
            key={uid}
            {...itemProps}
          />
        )
      })
  }

  render () {
    const { fileList, maxFile, disabled, dragUploaderProps } = this.props
    const isAnyFileUploading = getIsAnyFileUploading(fileList)
    const hasAnyErrorFile = getHasAnyErrorFile(fileList)
    const isHideUploadButton = disabled || (fileList.length >= maxFile) || isAnyFileUploading || hasAnyErrorFile
    return (
      <div
        className={styles.UploadList}
        data-role='upload-list'
      >
        {
          this.renderItems()
        }
        {
          <UploadButton
            key='button'
            className={cx(styles.UploadListItem, { hide: isHideUploadButton })}
            dragUploaderProps={dragUploaderProps}
          />
        }
      </div>
    )
  }
}

export default SortableContainer(UploadList)
